import React, { useEffect, useState } from "react";
import SideBar from "./DatabaseGUIComponents/SideBar/SideBar";
import QuerySection from "./DatabaseGUIComponents/QuerySection/QuerySection.js";
import "./DatabaseGUI.scss";
import { useSearchParams } from "react-router-dom";
import { getDatabaseType, closeDatabaseGuiConnection } from "../../../../api/database_gui.js";
import { decodeResourceParams } from "../../../../utils/urlEncoder";
import ConnectionErrorPage from "./DatabaseGUIComponents/ConnectionError/ConnectionErrorPage.js";
import { useInactivityTimeout } from "../../../../utils/useInactivityTimeout.js";

const DatabaseGUI = () => {
  const [searchParams] = useSearchParams();
  const encodedData = searchParams.get("data");
  let decodedParams = {};
  
  if (encodedData) {
    decodedParams = decodeResourceParams(encodedData) || {};
  }
  
  const resource_id = decodedParams.resource_id || searchParams.get("resource_id");
  const systemuser_id = decodedParams.systemuser_id || searchParams.get("systemuser_id");
  const app_type = decodedParams.app_type || searchParams.get("app_type");
  const token_key = decodedParams.token_key || searchParams.get("token_key");

  const [connectionError, setConnectionError] = useState(null);
  const [connectionStatusCode, setConnectionStatusCode] = useState(null);
  const [checkedConnection, setCheckedConnection] = useState(false); 
  const [databaseInfo, setDatabaseInfo] = useState();
  const [resourceAccessId, setResourceAccessID] = useState();
  const [selectedDatabase, setSelectedDatabase] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Timeout related state
  const [timeoutMinutes, setTimeoutMinutes] = useState(5);
  const [sessionTimedOut, setSessionTimedOut] = useState(false);

  // Function to trigger QueryRunSection refresh
  const triggerQueryRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleTimeout = () => {
    setSessionTimedOut(true);
  
    setConnectionError("You were logged out due to inactivity. Please relaunch the resource to continue.");
    setConnectionStatusCode(999);
  
    if (resourceAccessId) {
      closeDatabaseGuiConnection(resourceAccessId);
    }
  };
  

  useEffect(() => {
    const fetchDatabaseInfo = async () => {
      try {
        const { data, error } = await getDatabaseType(resource_id, systemuser_id, app_type);

        if (error) {
          setConnectionError(error.error_message || "Could not connect to database");
          setConnectionStatusCode(error.error_code || error.http_status || 500);
          return;
        }
        if (data?.connection_failed) {
          setConnectionError(data.resolution);
          setConnectionStatusCode(data.error_code || 500);
          return;
        } else {
          setDatabaseInfo(data.database_type);
          setResourceAccessID(data.resource_access_report_id);
          
          // Set timeout configuration if available
          if (data.inactive_time_allowed) {
            setTimeoutMinutes(data.inactive_time_allowed);
          }
          
          // Store resource_access_report_id in sessionStorage (same as file_manager)
          if (data.resource_access_report_id) {
            if (token_key) {
              sessionStorage.setItem(token_key, data.resource_access_report_id);
            }
          }
          
          // Set page title based on database type
          const databaseType = data.database_type;
          if (databaseType) {
            document.title = `${databaseType} - Database GUI`;
          }
        }
      } catch (error) {
        if (error.response?.data?.error_message) {
          setConnectionError(error.response.data.error_message);
        } else {
          setConnectionError("Something went wrong. Please try again.");
        }
      } finally {
        setCheckedConnection(true);
      }
    };

    fetchDatabaseInfo();
  }, [resource_id, systemuser_id, app_type]);

  useEffect(() => {
    if (!resourceAccessId) return;
    const handleBeforeUnload = (event) => {
      // Don't prevent the default behavior or set returnValue
      // This prevents the browser confirmation dialog
      // event.preventDefault();
      // event.returnValue = ''; 

      localStorage.setItem('unload_triggered', new Date().toISOString());
      closeDatabaseGuiConnection(resourceAccessId);
    };
    
    window.addEventListener("beforeunload", handleBeforeUnload); 

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [resourceAccessId]);

  // Initialize timeout hook
  const timeoutHook = useInactivityTimeout(timeoutMinutes, handleTimeout);

  // Start timeout when connection is established
  useEffect(() => {
    if (resourceAccessId && timeoutHook && timeoutHook.startTimeout) {
      timeoutHook.startTimeout();
    }
  }, [resourceAccessId, timeoutHook]);

  if (!checkedConnection) {
    return null;
  }

  if (connectionError) {
    return <ConnectionErrorPage message={connectionError} statusCode={connectionStatusCode} />;
  }

  // Show connection error page when session times out
  if (sessionTimedOut) {
    return <ConnectionErrorPage message="Session timeout reached. Please reconnect to continue." />;
  }

  return (
    <div className="database-gui">
      {/* <Header /> */}
      <SideBar
        className="database-gui-sidebar ff-poppins"
        resourceId={resource_id}
        systemUserId={systemuser_id}
        databaseInfo={databaseInfo} 
        setDatabaseInfo={setDatabaseInfo}
        selectedDatabase={selectedDatabase}
        setSelectedDatabase={setSelectedDatabase}
        triggerQueryRefresh={triggerQueryRefresh}
        resourceAccessID={resourceAccessId}
      />
      <QuerySection
        className="database-gui-query-section ff-poppins"
        resourceId={resource_id}
        systemUserId={systemuser_id}
        selectedDatabase={selectedDatabase}
        setSelectedDatabase={setSelectedDatabase}
        databaseInfo={databaseInfo}
        resourceAccessID={resourceAccessId}
        refreshTrigger={refreshTrigger}
      />
    </div>
  );
};

export default DatabaseGUI;
